import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getRanking } from "../lib/api";
import { parseView } from "../lib/retail";
import { ladderHeights, placeMark, formatValue, shortName, boardState, myEntry, type BoardKey } from "../lib/ranking";
import { fmt1 } from "../lib/format";
import type { RankingMonth, StoreRanking, RankBoard, SetsBoard } from "../lib/types";

/**
 * Вкладка «Рейтинг» (этап 4). Три доски внутри магазина: средний чек, выручка за смену, комплектные продажи.
 * Сотрудница — анонимно (лесенка без имён + «вы — N место из M»); владелец — с именами.
 * В режиме «сотрудница» владелец видит её анонимный вид, как видит она.
 */
export function RankingScreen() {
  const { me, t, month, view, retail } = useAuth();
  const [data, setData] = useState<RankingMonth | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const isAdmin = !!me?.is_admin;
  const v = isAdmin ? parseView(view) : { kind: "all" as const };
  const storeParam = v.kind === "store" ? v.store : v.kind === "person" ? (retail?.rows.find(r => r.person_id === v.personId)?.store ?? null) : null;

  useEffect(() => {
    if (!month) { setData(null); return; }
    let cancelled = false;
    setLoading(true); setError(null);
    getRanking(month, storeParam)
      .then(r => { if (!cancelled) setData(r); })
      .catch((e: any) => { if (!cancelled) { setData(null); setError(e.message || "ERROR"); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month, storeParam]);

  if (loading && !data) return <div className="p-6 text-sm text-slate-500">{t("err_loading")}</div>;
  if (error === "NO_DATA" || (!data && !loading)) return <div className="p-4"><Hint text={t("no_data_title")} /></div>;
  if (error) return <div className="p-6 text-sm text-rose-600">{error}</div>;
  if (!data) return null;

  // Владелец в режиме «сотрудница»: её анонимный вид — своя строка помечается по person_id.
  const anonFor = isAdmin && v.kind === "person" ? v.personId : null;

  return (
    <section className="p-4 space-y-3">
      <div className="text-[11px] text-slate-500 leading-relaxed px-1">{t("rank_how_common")}</div>
      {data.stores.map(s => (
        <StoreBoards key={s.store} s={s} withNames={s.named && anonFor === null} anonFor={anonFor} showStoreTitle={data.stores.length > 1} />
      ))}
    </section>
  );
}

function Hint({ text }: { text: string }) {
  return <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-center text-xs text-amber-800">{text}</div>;
}

function StoreBoards({ s, withNames, anonFor, showStoreTitle }: { s: StoreRanking; withNames: boolean; anonFor: string | null; showStoreTitle: boolean }) {
  const { t } = useAuth();
  const mark = (b: RankBoard): RankBoard => anonFor === null ? b : {
    ...b,
    entries: b.entries.map(e => ({ me: e.person_id === anonFor, value: e.value, bra_checks: e.bra_checks })),
    me_place: (() => { const i = b.entries.findIndex(e => e.person_id === anonFor); return i >= 0 ? i + 1 : null; })(),
  };
  const sets: SetsBoard = anonFor === null ? s.boards.sets : (() => {
    const base = mark(s.boards.sets) as SetsBoard;
    const below = s.boards.sets.below_threshold?.find(x => x.person_id === anonFor);
    const mine = s.boards.sets.entries.find(e => e.person_id === anonFor);
    return { ...base, threshold: s.boards.sets.threshold, below_threshold: undefined,
      my_bra_checks: mine?.bra_checks ?? below?.bra_checks ?? null, my_score: mine?.value ?? below?.score ?? null, me_eligible: !!mine };
  })();
  return (
    <>
      {showStoreTitle && <div className="text-xs uppercase tracking-wide text-slate-500 pt-1">{s.store_label}</div>}
      <BoardCard k="avg_check" b={mark(s.boards.avg_check)} withNames={withNames} title={t("rank_title_avg")} sub={t("rank_sub_avg")} unit={t("rank_unit_grn")} />
      <BoardCard k="revenue_per_shift" b={mark(s.boards.revenue_per_shift)} withNames={withNames} title={t("rank_title_rps")} sub={t("rank_sub_rps")} unit={t("rank_unit_per_shift")} />
      <BoardCard k="sets" b={sets} withNames={withNames} title={t("rank_title_sets")} sub={t("rank_sub_sets", { t: sets.threshold })} unit={t("rank_unit_points")} rule={t("rank_sets_rule")} />
    </>
  );
}

const HOW_KEY: Record<BoardKey, string> = { avg_check: "rank_how_avg", revenue_per_shift: "rank_how_rps", sets: "rank_how_sets" };

function BoardCard({ k, b, withNames, title, sub, unit, rule }: { k: BoardKey; b: RankBoard | SetsBoard; withNames: boolean; title: string; sub: string; unit: string; rule?: string }) {
  const { t } = useAuth();
  const state = boardState(b);
  const mine = myEntry(b);
  const sets = k === "sets" ? (b as SetsBoard) : null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-3">
      <div className="text-center">
        <div className="text-sm font-semibold text-slate-800">{title}</div>
        <div className="text-[10px] text-slate-500">{sub}</div>
        {rule && <div className="text-[10px] text-slate-400">{rule}</div>}
      </div>

      {state.kind === "no_data" && <div className="mt-2"><Hint text={t("rank_no_data")} /></div>}
      {state.kind === "too_few" && <div className="mt-2"><Hint text={t("rank_min_two")} /></div>}
      {state.kind === "ok" && <Ladder b={b} withNames={withNames} />}

      {!withNames && mine && b.me_place !== null && (
        <div className="mt-1.5 bg-slate-50 rounded-lg px-2 py-1.5 border border-slate-200 flex items-center justify-between">
          <div>
            <span className="text-[10px] uppercase tracking-wide text-teal-700 font-semibold">{t("rank_you")}</span>
            <span className="text-sm font-bold text-slate-900 ml-1">{t("rank_place", { p: b.me_place, n: b.total })}</span>
          </div>
          <div className="text-sm font-bold text-slate-900 tabular-nums">{formatValue(k, mine.value)} <span className="text-[10px] font-normal text-slate-500">{unit}</span></div>
        </div>
      )}
      {!withNames && sets && !sets.me_eligible && sets.my_bra_checks != null && (
        <div className="mt-1.5 text-[11px] text-slate-600 text-center tabular-nums">
          {t("rank_not_eligible", { n: sets.my_bra_checks, t: sets.threshold })}
          {sets.my_score != null && <> · {t("rank_my_score", { s: fmt1(sets.my_score) })}</>}
        </div>
      )}

      {withNames && state.kind === "ok" && (
        <div className="mt-2 bg-slate-50 rounded-lg p-2 border border-slate-200 space-y-1">
          {b.entries.map((e, idx) => (
            <div key={e.person_id ?? idx} className="flex items-center justify-between text-xs">
              <span className="text-slate-700">{placeMark(idx + 1)} {e.display_name ?? e.person_id}{e.bra_checks !== undefined && <span className="text-slate-400"> · {t("rank_bra_checks", { n: e.bra_checks })}</span>}</span>
              <span className="font-semibold text-slate-900 tabular-nums">{formatValue(k, e.value)} <span className="text-slate-500 font-normal">{unit}</span></span>
            </div>
          ))}
        </div>
      )}
      {/* пояснение, как считается (просьба владельца 28.10.2026) */}
      <details className="mt-2">
        <summary className="text-[11px] text-slate-500 cursor-pointer select-none list-none">ⓘ {t("rank_how")}</summary>
        <div className="text-[11px] text-slate-600 mt-1 leading-relaxed">{t(HOW_KEY[k])}</div>
      </details>

      {withNames && sets && sets.below_threshold && sets.below_threshold.length > 0 && (
        <div className="mt-2 text-[11px] text-slate-500">
          <div className="uppercase tracking-wide text-[10px] mb-0.5">{t("rank_below_title")}</div>
          {sets.below_threshold.map(x => (
            <div key={x.person_id} className="flex justify-between tabular-nums">
              <span>{x.display_name ?? x.person_id} · {t("rank_bra_checks", { n: x.bra_checks })}</span>
              <span>{x.score == null ? "—" : fmt1(x.score)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Лесенка по убыванию: первое место самое высокое, дальше ниже. Цвет = идентичность: «вы» teal, остальные серые. */
function Ladder({ b, withNames }: { b: RankBoard; withNames: boolean }) {
  const heights = ladderHeights(b.total);
  return (
    <div className="flex items-end justify-center gap-1.5 px-2 mt-2" style={{ height: `${(heights[0] ?? 88) + 24 + (withNames ? 16 : 0)}px` }}>
      {b.entries.map((e, i) => {
        const place = i + 1;
        const bg = e.me ? "bg-teal-500" : "bg-slate-200";
        const col = e.me ? "text-white" : "text-slate-500";
        return (
          <div key={e.person_id ?? i} className="flex-1 max-w-[64px] flex flex-col items-center justify-end h-full">
            <div className="text-base leading-none mb-0.5">{placeMark(place)}</div>
            <div className={`w-full ${bg} rounded-t-lg flex items-start justify-center pt-1`} style={{ height: `${heights[i]}px` }}>
              <span className={`text-sm font-bold ${col}`}>{place}</span>
            </div>
            {withNames && <div className="text-[9px] text-slate-600 mt-1 text-center leading-tight truncate w-full">{shortName(e.display_name, e.person_id ?? "")}</div>}
          </div>
        );
      })}
    </div>
  );
}
